/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 3 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import type { ComponentProps, MouseEvent, ReactElement } from "react";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { useSelector, shallowEqual } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useRef, useEffect, useState, useContext, useCallback, useMemo } from "react";

import { DuplicateTakeoffModal, TakeoffProperties, getTakeoffListColums } from "@Features/estimation";
import { convertDate } from "@Services/luxonHelper";
import { estimateTakeoffsListUrl } from "@Services/dataManager";
import CreateNewTakeoffModal from "@Features/viewer-modals/components/Modal/CreateNewTakeoffModal";
import {
  getDisplayQuantity,
  duplicateTakeoff,
  takeoffSources,
  getTakeoffsListData,
  updateTakeoff,
  deleteTakeoff,
  setTakeoffData,
  loadMarkupStylesViewer,
  prepareMarkupColor,
  upgradeTakeOffAttachedQuery,
  prepareEstimationFieldNeedToRefresh,
  getEstimateNumberValueBasedOnSettings,
  isEstimateRequiredPermission,
} from "@Actions/estimation";
import { setEstimateFieldsNeedtoRefresh, estimateFieldsNeedToRefreshIntialState } from "@Reducers/EstimateList";

import ToolTipComponent from "@CubeComponents/html/tooltip";
import { HoopsViewerContext, CubeGlobal } from "@Features/viewer-core";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { showToastNotification, type ToastNotificationPayload } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import { useLocation, useParams } from "react-router-dom";
import { getEstimateData } from "@Actions/EstimateList";
import { setModalStatus } from "@Reducers/Viewer";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { folderPermissions } from "@Helpers/constants";
import IconComponent from "@CubeComponents/icons";
import { getFileversionIdAndNodeOffsetIdMap } from "@Services/cubeViewHelper";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import type React from "react";
import type {
  GridClickEvent,
  SlickgridReactInstanceX,
  ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { getIsViewerDependencyLoaded } from "@Services/utils/helpers";
import { Box } from "@mui/material";
import Text from "@CubeComponents/html/Text/Text";
import type { RootState } from "src/reducers";
import type { EstimateData } from "@ServerTypes/estimates/estimates.schema";
import type {
  EstimateFieldsNeedToRefresh,
  MeasurementsModalStatus,
  TakeoffListItem,
  TakeoffSource,
} from "@Helpers/types/estimation/takeoffTypes";
import type {
  AutomationQuery,
  TakeoffData,
  TakeoffListItemAttached,
  TakeoffMeasurement,
} from "@ServerTypes/takeoffs/takeoffs.schema";
import { Selection } from "@ts3d-hoops/web-viewer";
import type { Formatter } from "slickgrid-react";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

const { RESOURCE_MODIFY } = folderPermissions();

type TakeoffsListProps = {
  source: TakeoffSource;
  estimateId?: ObjectIdString;
  fileVersionId?: string;
  removeTakeoffsListMarkups?: (takeoffsList: TakeoffListItem[]) => void;
};

/**
 * A takeoff row as it comes out of the SlickGrid dataView. Both grid modes are fed by
 * `getTakeoffsList` — the viewer mode from the `takeoffsList` redux slice, the estimate mode from
 * the same endpoint as a remote data source — so the row is exactly the server `TakeoffData`
 * (its `takeOffItemId` is already `objectIdString`).
 */
type TakeoffGridRow = TakeoffData;

type SlickGridHandle = {
  slickGrid: {
    getSelectedRows: () => number[];
    getDataItem: (row: number) => TakeoffGridRow;
    setSelectedRows: (rows: number[]) => void;
  };
  refresh: () => void;
};

type ExecuteQueryModalData = {
  takeOffItemId: ObjectIdString;
  automationQuery?: AutomationQuery;
};

type ExecuteQueryModalState = {
  show: boolean;
  data: ExecuteQueryModalData | null;
};

type CreateTakeoffModalState = {
  show: boolean;
  data: { name?: string; description?: string } | null;
};

function TakeoffsList({ source, estimateId, fileVersionId }: TakeoffsListProps) {
  const { t } = useTranslation();
  // Entry point: route params are outside the type system; the backend route schema validates them.
  const params = useParams<{ versionId: ObjectIdString }>();
  const { isModelStructureReady, isSettingsApplied, viewerImpl } = useContext(HoopsViewerContext);
  const hwv = viewerImpl?.hwv;
  const dispatch = useDispatch();
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location?.search);

  const { estimateFieldsNeedToRefresh, takeoffsList, estimateData, measurementsModalStatus, HoopsModalStatus } =
    useSelector(
      (state: RootState) => ({
        estimateFieldsNeedToRefresh: state.estimateFieldsNeedToRefresh as EstimateFieldsNeedToRefresh,
        takeoffsList: state.takeoffsList,
        estimateData: state.estimateData as EstimateData,
        measurementsModalStatus: state.measurementsModalStatus as MeasurementsModalStatus,
        HoopsModalStatus: state.HoopsModalStatus,
      }),
      shallowEqual,
    );

  const actionPermissionAllowed = isEstimateRequiredPermission(
    estimateData as Parameters<typeof isEstimateRequiredPermission>[0],
    RESOURCE_MODIFY.id,
  );
  // Entry point: the non-route branch is a raw URLSearchParams read; the backend route schema validates it.
  const estimateVersionId =
    source === takeoffSources.ESTIMATE ? params.versionId : (urlSearchParams.get("versionId") as ObjectIdString | null);

  const [openSidebar, setOpenSidebar] = useState(false);
  const [originalTakeoff, setOriginalTakeoff] = useState<TakeoffGridRow | null>(null);
  const [sidebarTakeoffId, setSidebarTakeoffId] = useState<ObjectIdString | null>(null);
  const [showCreateTakeoffModal, setShowCreateTakeoffModal] = useState<CreateTakeoffModalState>({
    show: false,
    data: null,
  });
  const [showDuplicateTakeoffModal, setShowDuplicateTakeoffModal] = useState(false);
  const [showConfirmExecuteQueryAgainModal, setShowConfirmExecuteQueryAgainModal] = useState<ExecuteQueryModalState>({
    show: false,
    data: null,
  });
  const [buttonState, setButtonState] = useState(false);
  const buttonIntialState = {
    add: Boolean(estimateVersionId) || !actionPermissionAllowed,
    edit: true,
    delete: true,
    duplicate: true,
    properties: true,
    isolate: true,
    highlight: true,
  };
  const [toolbarButtonDisableState, setToolbarButtonDisableState] = useState(buttonIntialState);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [gridQuery] = useState(
    source == takeoffSources.ESTIMATE
      ? {
          estimateId,
          params: {
            estimateId,
            versionId: estimateVersionId,
            skipSystemGenerated: false,
          },
        }
      : {},
  );

  const dataGrid = useRef<SlickGridHandle | null>(null);
  const takeoffsListRef = useRef<TakeoffData[]>(takeoffsList);
  const measurementsModalStatusRef = useRef<MeasurementsModalStatus>(measurementsModalStatus);
  const HoopsModalStatusRef = useRef(HoopsModalStatus);

  // Refs so grid cell formatters (captured once in gridColumns) and toolbar templates always read the
  // latest values instead of the closures captured on first render.
  const estimateDataRef = useRef(estimateData);
  estimateDataRef.current = estimateData;
  const estimateVersionIdRef = useRef(estimateVersionId);
  estimateVersionIdRef.current = estimateVersionId;
  const toolbarClickHandlerRef = useRef<(toolClicked: string) => void>(() => undefined);

  const sepratorButtonTemplate = useCallback(() => <div id="seprator" className="w-px h-5 bg-text_light"></div>, []);

  const isolateBtnTemplate = useCallback(
    () => (
      <Box
        className="flex items-center h-6 w-6"
        id="isolate"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          toolbarClickHandlerRef.current("isolate");
        }}
      >
        <IconComponent
          name="Isolate"
          width="1.25rem"
          height="1.25rem"
          position="bottom"
          toolTipContent={t("Isolate")}
        />
      </Box>
    ),
    [t],
  );

  const highlightBtnTemplate = useCallback(
    () => (
      <Box
        className="flex items-center h-6 w-6"
        id="highlight"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          toolbarClickHandlerRef.current("highlight");
        }}
      >
        <IconComponent
          name="Highlight"
          width="1.25rem"
          height="1.25rem"
          position="bottom"
          toolTipContent={t("Focus")}
        />
      </Box>
    ),
    [t],
  );

  const showButtonTemplate = useCallback(
    () => (
      <Box
        className="flex items-center h-6 w-6"
        id="show"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          toolbarClickHandlerRef.current("show");
        }}
      >
        <IconComponent name="Show" width="1.25rem" height="1.25rem" position="bottom" toolTipContent={t("Show")} />
      </Box>
    ),
    [t],
  );

  const tools = useMemo(() => {
    const baseTools: Array<{
      id: string;
      name?: string;
      prefixIcon?: string;
      disabled?: boolean;
      template?: () => ReactElement;
      rightAllign?: boolean;
    }> = [
      {
        id: "create_new_takeoff",
        name: t("Create"),
        prefixIcon: "add",
        disabled: toolbarButtonDisableState.add,
      },
      {
        id: "edit_takeoff",
        name: t("Edit"),
        prefixIcon: "editProfile",
        disabled: toolbarButtonDisableState.edit,
      },
      {
        id: "delete_takeoff",
        name: t("Delete"),
        prefixIcon: "delete",
        disabled: toolbarButtonDisableState.delete,
      },
      {
        id: "duplicate_takeoff",
        name: t("Duplicate"),
        prefixIcon: "Duplicate",
        disabled: toolbarButtonDisableState.duplicate,
      },
    ];

    if (source === takeoffSources.VIEWER) {
      baseTools.push(
        {
          id: "seprator",
          template: sepratorButtonTemplate,
          disabled: true,
        },
        {
          id: "isolate",
          template: isolateBtnTemplate,
          disabled: toolbarButtonDisableState.isolate,
        },
        {
          id: "highlight",
          template: highlightBtnTemplate,
          disabled: toolbarButtonDisableState.highlight,
        },
        {
          id: "show",
          template: showButtonTemplate,
        },
      );
    }
    return baseTools;
  }, [
    source,
    t,
    toolbarButtonDisableState,
    sepratorButtonTemplate,
    isolateBtnTemplate,
    highlightBtnTemplate,
    showButtonTemplate,
  ]);

  // Properties panel is handled via propertiesPanel prop on SlickGrid1

  const fetchTakeoffsList = useCallback(() => {
    if (!estimateId) {
      return;
    }
    dispatch(
      getTakeoffsListData({
        estimateId,
        versionId: estimateVersionId ?? undefined,
        skipSystemGenerated: false,
      }),
    );
  }, [estimateId, estimateVersionId]);

  const fetchEstimateData = () => {
    if (!estimateId) {
      return;
    }
    dispatch(
      getEstimateData({
        estimateId,
        versionId: estimateVersionId || undefined,
      }),
    );
  };

  const refreshTakeOffAndEstimate = () => {
    source === takeoffSources.ESTIMATE && dataGrid.current?.refresh();
    source === takeoffSources.VIEWER && fetchTakeoffsList();
    fetchEstimateData();
  };

  const closeNewTakeoffModal = ({ refresh }: { refresh?: boolean } = {}) => {
    setShowCreateTakeoffModal({ show: false, data: null });
    refresh && refreshTakeOffAndEstimate();
  };

  const closeDuplicateTakeoffModal = () => {
    setShowDuplicateTakeoffModal(false);
    setOriginalTakeoff(null);
  };

  const openTakeoffsSidebar = async (rowData: TakeoffGridRow[]) => {
    if (!rowData[0]) {
      return;
    }
    setOpenSidebar(true);
    const takeOffItemId = rowData[0].takeOffItemId;
    setSidebarTakeoffId(takeOffItemId);
  };

  const closeTakeoffsSidebar = () => {
    setOpenSidebar(false);
    setSidebarTakeoffId(null);
    dataGrid.current?.slickGrid.setSelectedRows([]);
  };

  const copyTakeoff = async (linkMeasurements: boolean, linkItems: boolean) => {
    if (!originalTakeoff || !estimateId) return;
    const data = {
      takeOffItemId: originalTakeoff.takeOffItemId,
      name: originalTakeoff.name,
      description: originalTakeoff.description,
      linkMeasurements,
      linkItems,
      estimateId,
    };
    await duplicateTakeoff(data);
    if (source === takeoffSources.VIEWER && originalTakeoff.measurements?.length && linkMeasurements) {
      const setData = prepareEstimationFieldNeedToRefresh({
        HoopsModalStatus,
        measurements: true,
        estimateFieldsNeedToRefresh,
      });
      dispatch(setEstimateFieldsNeedtoRefresh(setData));
    }

    closeDuplicateTakeoffModal();
    refreshTakeOffAndEstimate();
  };

  /** ******************************************** LOAD MARKUPS IN HOOPS VIEWER ****************************************************** */

  const removeMarkupFromViewer = (markup: { cubeUniqueId: string }) => {
    if (!viewerImpl?.hwv) return;
    viewerImpl.hwv.measureManager.removeMeasurement(markup);
  };

  const stylingMarkups = (takeoffData: TakeoffData, markupsByCubeUniqueId: Map<string, { cubeUniqueId: string }>) => {
    const {
      lineColor: _lineColor,
      fillColor: _fillColor,
      lineThickness,
      fillOpacity,
      symbolSize,
      countSymbol,
    } = takeoffData;
    const lineColor = prepareMarkupColor(_lineColor!);
    const fillColor = prepareMarkupColor(_fillColor!);

    takeoffData.measurements?.forEach((measurement: TakeoffMeasurement) => {
      const viewerMarkup = markupsByCubeUniqueId.get(measurement.measurementId);
      viewerMarkup &&
        loadMarkupStylesViewer({
          viewerImpl,
          viewerMarkup,
          lineThickness,
          fillOpacity,
          symbolSize,
          countSymbol,
          lineColor,
          fillColor,
          isModelStructureReady,
          isSettingsApplied,
          // @ts-expect-error -- `hwv` is passed to a component whose props declare only `viewerImpl`. REAL GAP: either the prop is dead or the declaration is missing it.
          hwv: viewerImpl?.hwv,
        });
    });
  };

  const getTakeoffsAttachedMeasurements = (takeoffs: TakeoffData[], mapper: { [fileVersionId: string]: number }) => {
    const staticMarkUpMeasurement: TakeoffMeasurement[] = [];
    const dynamicMeasurementNodeIds: number[] = [];
    for (const takeoff of takeoffs) {
      const measurements = takeoff.measurements;
      if (!measurements) continue;
      for (const measurement of measurements) {
        if (measurement.fileVersionId != fileVersionId) continue;
        if (measurement.markup != "{}") {
          staticMarkUpMeasurement.push(measurement);
        } else if (measurement.elements) {
          const element = measurement.elements;
          dynamicMeasurementNodeIds.push(element.nodeId + mapper[element.fileVersionId]!);
        }
      }
    }
    return { staticMarkUpMeasurement, dynamicMeasurementNodeIds };
  };

  const selectNodesInViewer = (nodeIds: number[]) => {
    if (!nodeIds.length || !viewerImpl?.hwv) return;
    try {
      const selectionItems = nodeIds.map((nodeId: number) => Selection.SelectionItem.create(nodeId));
      viewerImpl.hwv.selectionManager.add(selectionItems, false);
    } catch (e) {
      console.log(e);
      Promise.reject(e);
    }
  };

  const removeNodesFromViewer = (nodeIds: number[]) => {
    if (!nodeIds.length || !viewerImpl?.hwv) return;
    try {
      const targetNodeIds = new Set(nodeIds);
      const selectionItemsToRemove = viewerImpl.hwv.selectionManager
        .getResults()
        .filter((item: { getNodeId: () => number }) => targetNodeIds.has(item.getNodeId()));
      if (selectionItemsToRemove.length) {
        viewerImpl.hwv.selectionManager.remove(selectionItemsToRemove);
      }
    } catch (e) {
      console.log(e);
      Promise.reject(e);
    }
  };

  const removeAllMeasurementsMarkupsFromModel = (takeoffsData?: TakeoffData[]) => {
    if (
      !viewerImpl?.hwv ||
      !getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv }) ||
      !fileVersionId
    ) {
      return;
    }
    takeoffsData = takeoffsData || takeoffsList;
    const mapper: { [fileVersionId: string]: number } = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
    const { staticMarkUpMeasurement, dynamicMeasurementNodeIds } = getTakeoffsAttachedMeasurements(
      takeoffsData,
      mapper,
    );

    removeNodesFromViewer(dynamicMeasurementNodeIds);

    if (staticMarkUpMeasurement.length) {
      const allMeasurementsinViewer: Array<{ cubeUniqueId: string }> =
        viewerImpl.hwv.measureManager.getAllMeasurements();
      const staticMeasurementIds = new Set(
        staticMarkUpMeasurement.map((measurement: TakeoffMeasurement) => measurement.measurementId),
      );
      allMeasurementsinViewer
        .filter((record: { cubeUniqueId: string }) => staticMeasurementIds.has(record.cubeUniqueId))
        .forEach((markup: { cubeUniqueId: string }) => removeMarkupFromViewer(markup));
    }
  };

  const toggleShowMarkupInViewer = (
    takeoffsData: TakeoffData[],
    mapper?: { [fileVersionId: string]: number },
    skipRemoveMarkups: boolean = false,
  ) => {
    if (
      !getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv }) ||
      !fileVersionId
    ) {
      return;
    }
    if (!skipRemoveMarkups) {
      removeAllMeasurementsMarkupsFromModel();
    }
    mapper = mapper || getFileversionIdAndNodeOffsetIdMap(fileVersionId);
    const { staticMarkUpMeasurement, dynamicMeasurementNodeIds } = getTakeoffsAttachedMeasurements(
      takeoffsData,
      mapper!,
    );

    selectNodesInViewer(dynamicMeasurementNodeIds);

    if (staticMarkUpMeasurement.length) {
      const markups = staticMarkUpMeasurement.map((record: TakeoffMeasurement) => JSON.parse(record.markup ?? "{}"));
      viewerImpl?.loadMarkups?.(markups);
    }
    const allMeasurementsinViewer: Array<{ cubeUniqueId: string }> =
      viewerImpl?.hwv.measureManager.getAllMeasurements();
    const markupsByCubeUniqueId = new Map<string, { cubeUniqueId: string }>();
    for (const record of allMeasurementsinViewer ?? []) {
      if (!markupsByCubeUniqueId.has(record.cubeUniqueId)) {
        markupsByCubeUniqueId.set(record.cubeUniqueId, record);
      }
    }
    takeoffsData.forEach((takeoff: TakeoffData) => {
      stylingMarkups(takeoff, markupsByCubeUniqueId);
    });
    return { staticMarkUpMeasurement, dynamicMeasurementNodeIds };
  };

  const handleIsolateClick = async (takeoffsData: TakeoffData[]) => {
    if (
      !fileVersionId ||
      !viewerImpl?.hwv ||
      !getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv })
    )
      return;
    removeAllMeasurementsMarkupsFromModel();
    const mapper: { [fileVersionId: string]: number } = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
    const { dynamicMeasurementNodeIds } =
      toggleShowMarkupInViewer(takeoffsData, mapper) ?? getTakeoffsAttachedMeasurements(takeoffsData, mapper);

    await CubeGlobal.isolateNodes(dynamicMeasurementNodeIds);
  };

  const handleFocusClick = async (takeoffsData: TakeoffData[]) => {
    if (
      !fileVersionId ||
      !hwv ||
      !getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv })
    )
      return;
    removeAllMeasurementsMarkupsFromModel();
    const mapper: { [fileVersionId: string]: number } = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
    const { dynamicMeasurementNodeIds } =
      toggleShowMarkupInViewer(takeoffsData, mapper) ?? getTakeoffsAttachedMeasurements(takeoffsData, mapper);
    const nodeIds = dynamicMeasurementNodeIds;

    interface BoundingBox {
      addBox: (val: BoundingBox) => void;
      copy: () => BoundingBox;
    }
    const abc: Array<{ getBoundingBox: () => BoundingBox }> = viewerImpl.hwv.measureManager.getAllMeasurements();
    let parentBox: BoundingBox | undefined;
    if (abc.length) {
      const ppp = abc[0]!.getBoundingBox();
      parentBox = ppp.copy();
      abc.slice(1).map((val: { getBoundingBox: () => BoundingBox }) => {
        parentBox?.addBox(val.getBoundingBox());
      });
    }

    if (nodeIds.length) {
      const dynM: BoundingBox = await viewerImpl.hwv.model.getNodesBounding(nodeIds);
      if (!parentBox) {
        parentBox = dynM.copy();
      } else {
        parentBox.addBox(dynM);
      }
    }

    if (parentBox) {
      viewerImpl.hwv.view.fitBounding(parentBox, 1000);
    }
  };

  /** ************************************************ GRID EVENT FUNCTIONS ***************************************************** */

  const updateTakeOffData = async (newTakeoffDetails: { name: string; description?: string }) => {
    if (!estimateId) return;
    const selectedRows = dataGrid.current?.slickGrid.getSelectedRows() ?? [];
    const selectedTakeoff = selectedRows
      .map((data: number) => dataGrid.current?.slickGrid.getDataItem(data))
      .filter(Boolean) as TakeoffGridRow[];
    if (!selectedTakeoff || !selectedTakeoff[0]) return;
    const response = await updateTakeoff({
      takeOffItemId: selectedTakeoff[0].takeOffItemId,
      ...newTakeoffDetails,
      estimateId,
    });
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("edit_takeoff", { name: newTakeoffDetails.name }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      if (selectedTakeoff[0].measurements?.length) {
        const setData = prepareEstimationFieldNeedToRefresh({
          HoopsModalStatus,
          measurements: true,
          estimateFieldsNeedToRefresh,
        });
        dispatch(setEstimateFieldsNeedtoRefresh(setData));
      }
    } else {
      dispatch(showToastNotification({ content: SOMETHING_WENT_WRONG, type: TOAST_TYPE_ERROR }));
    }
  };

  const recordClick = (params: { detail: { args: { cell: number; row: number } } }) => {
    if (source === takeoffSources.ESTIMATE || params.detail.args.cell === 0) return;

    const rowData = dataGrid.current?.slickGrid.getDataItem(params.detail.args.row);
    if (!rowData) return;
    dispatch(setTakeoffData({ takeOffItemId: rowData.takeOffItemId }));
    dispatch(
      setModalStatus({
        ...HoopsModalStatusRef.current,
        Takeoffs: {
          ...HoopsModalStatusRef.current.Takeoffs,
          currentStage: "Takeoff_Detail",
        },
      }),
    );
  };

  const closeDeleteModalPopup = () => {
    setOpenDeleteModal(false);
  };

  const removeTakeoffHandler = async () => {
    if (!estimateId) return;
    setButtonLoading(true);
    const selectedRows = dataGrid.current?.slickGrid.getSelectedRows() ?? [];
    const data = selectedRows
      .map((data: number) => dataGrid.current?.slickGrid.getDataItem(data))
      .filter(Boolean) as TakeoffGridRow[];
    const measurements = data.flatMap((takeoff) => takeoff.measurements ?? []);
    const response = await deleteTakeoff({
      takeOffItemId: data.map((takeoff: TakeoffGridRow) => takeoff.takeOffItemId),
      estimateId,
    });
    if (source === takeoffSources.VIEWER) {
      if (measurements?.length) {
        const setData = prepareEstimationFieldNeedToRefresh({
          HoopsModalStatus,
          measurements: true,
          estimateFieldsNeedToRefresh,
        });
        dispatch(setEstimateFieldsNeedtoRefresh(setData));
      }
      removeAllMeasurementsMarkupsFromModel(data);
    }
    refreshTakeOffAndEstimate();
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("Selected Takeoff(s) have been deleted successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
    setButtonLoading(false);
    closeDeleteModalPopup();
  };

  const modelContent = () => (
    <Text sx={{ whiteSpace: "normal" }} className="font-normal text-sm">
      {t("Are you sure you want to delete selected Takeoff(s)?")}
    </Text>
  );
  const footer = () => (
    <div className="flex gap-4">
      <ButtonComponent onClick={removeTakeoffHandler} buttonType={BUTTON_TYPES.GRADIENT_BUTTON} loading={buttonLoading}>
        <Text>{t("Confirm")}</Text>
      </ButtonComponent>
      <ButtonComponent
        onClick={closeDeleteModalPopup}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        disabled={buttonLoading}
      >
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  const deleteModal = () => (
    <ModalStateDialogComponent
      footerContent={footer()}
      headerTitle={<Text className="">{t("Delete Takeoffs")}</Text>}
      isVisible={openDeleteModal}
      onCancel={closeDeleteModalPopup}
      modelContent={modelContent()}
      width="28.125rem"
    />
  );

  const toolbarClickHandler = (toolClicked: string) => {
    const selectedRows = dataGrid.current?.slickGrid.getSelectedRows() ?? [];
    const selectedTakeoff = selectedRows
      .map((data: number) => dataGrid.current?.slickGrid.getDataItem(data))
      .filter(Boolean) as TakeoffGridRow[];
    if (toolClicked === "create_new_takeoff") {
      if (!estimateVersionId) {
        setShowCreateTakeoffModal((prev: CreateTakeoffModalState) => ({ ...prev, show: true }));
        return;
      }

      dispatch(
        showToastNotification({
          content: t("Takeoff can not be created while in preview mode"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return;
    }

    if (toolClicked === "edit_takeoff") {
      if (selectedTakeoff && selectedTakeoff.length === 1 && !estimateVersionId) {
        const firstTakeoff = selectedTakeoff[0];
        if (!firstTakeoff) {
          return;
        }
        const data: { name: string; description?: string } = { name: firstTakeoff.name };
        if (source == takeoffSources.ESTIMATE) {
          data.description = firstTakeoff.description;
        }
        setShowCreateTakeoffModal({ show: true, data });
        return;
      }

      if (estimateVersionId) {
        dispatch(
          showToastNotification({
            content: t("Takeoff can not be edited while in preview mode"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return;
      }

      dispatch(
        showToastNotification({
          content: t("Please select a takeoff to edit"),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }

    if (toolClicked === "duplicate_takeoff") {
      if (selectedTakeoff && selectedTakeoff.length === 1 && !estimateVersionId) {
        const firstTakeoff = selectedTakeoff[0];
        if (!firstTakeoff) {
          return;
        }
        setOriginalTakeoff(firstTakeoff);
        setShowDuplicateTakeoffModal(true);
        return;
      }

      if (estimateVersionId) {
        dispatch(
          showToastNotification({
            content: t("Takeoff can not be duplicated while in preview mode"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return;
      }

      dispatch(
        showToastNotification({
          content: t("Please select a takeoff to duplicate"),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }

    if (toolClicked === "delete_takeoff") {
      const deleteRows = dataGrid.current?.slickGrid.getSelectedRows() ?? [];
      const selectedTakeoff = deleteRows
        .map((data: number) => dataGrid.current?.slickGrid.getDataItem(data))
        .filter(Boolean) as TakeoffGridRow[];

      if (selectedTakeoff && selectedTakeoff.length && !estimateVersionId) {
        setOpenDeleteModal(true);
        return;
      }

      if (estimateVersionId) {
        dispatch(
          showToastNotification({
            content: t("Takeoff can not be deleted while in preview mode"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return;
      }

      dispatch(
        showToastNotification({
          content: t("Please select a takeoff to delete"),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }

    if (source === takeoffSources.ESTIMATE && (toolClicked === "takeoff_properties" || toolClicked === "properties")) {
      if (selectedTakeoff && selectedTakeoff.length === 1) {
        openTakeoffsSidebar(selectedTakeoff);
        return;
      }

      dispatch(
        showToastNotification({
          content: t("Please select a takeoff to access properties"),
          type: TOAST_TYPE_WARNING,
        }),
      );
    }

    if (toolClicked === "isolate") {
      handleIsolateClick(selectedTakeoff);
    }

    if (toolClicked === "highlight") {
      handleFocusClick(selectedTakeoff);
    }

    if (toolClicked === "show") {
      toggleShowMarkupInViewer(selectedTakeoff);
    }
  };
  toolbarClickHandlerRef.current = toolbarClickHandler;

  const selectionEvent = async () => {
    if (dataGrid.current) {
      const selectedTakeoffs = dataGrid.current.slickGrid
        .getSelectedRows()
        .map((data: number) => dataGrid.current?.slickGrid.getDataItem(data))
        .filter(Boolean) as TakeoffGridRow[];
      const totalRowsSelected = selectedTakeoffs.length;
      const isSystemGeneratedTakeoffSelected = selectedTakeoffs.find((record: TakeoffData) => record.isSystemGenerated);
      if (totalRowsSelected === 0) {
        setToolbarButtonDisableState((prev: typeof buttonIntialState) => ({
          ...prev,
          edit: true,
          delete: true,
          duplicate: true,
          isolate: true,
          highlight: true,
          properties: true,
        }));
      } else {
        if (!estimateVersionId && actionPermissionAllowed) {
          setToolbarButtonDisableState((prev: typeof buttonIntialState) => ({
            ...prev,
            edit: totalRowsSelected !== 1 || Boolean(isSystemGeneratedTakeoffSelected),
            delete: Boolean(isSystemGeneratedTakeoffSelected),
            duplicate: totalRowsSelected !== 1 || Boolean(isSystemGeneratedTakeoffSelected),
            isolate: false,
            highlight: false,
            properties: totalRowsSelected !== 1,
          }));
        } else {
          setToolbarButtonDisableState((prev: typeof buttonIntialState) => ({
            ...prev,
            edit: true,
            delete: true,
            duplicate: true,
            isolate: false,
            highlight: false,
            properties: totalRowsSelected !== 1,
          }));
        }
      }
    }
  };

  /** ********************************************** GRID COLUMN TEMPLATES ***************************************************** */

  const dateTemplate: Formatter<TakeoffData | TakeoffListItemAttached> = (_row, _cell, _value, _columnDef, params) => {
    const createdDate = "createdDate" in params ? params.createdDate : undefined;
    const dateFormat = convertDate(Number(createdDate), "dateForTooltip");

    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <>
        <div style={{ width: "fit-content" }}>
          <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
            <span>{convertDate(Number(createdDate), "dateForText")}</span>
          </ToolTipComponent>
        </div>
      </>,
    );

    return container;
  };

  const nameTemplate: Formatter<TakeoffData | TakeoffListItemAttached> = (_row, _cell, _value, _columnDef, params) => {
    const isSystemGenerated = "isSystemGenerated" in params ? params.isSystemGenerated : false;
    const container = document.createElement("div");
    container.classList.add("w-full");
    const root = createRoot(container);
    root.render(
      <div className="flex items-center justify-between w-full gap-1">
        <span className="text-ellipsis overflow-hidden whitespace-nowrap">{params.name}</span>
        {isSystemGenerated && (
          <IconComponent
            name="AutoGenerated"
            position="bottom"
            toolTipContent={t("System-generated via Model to Takeoff Wizard")}
            width="1.25rem"
            height="1.25rem"
          />
        )}
      </div>,
    );

    return container;
  };

  const quantityTemplate: Formatter<TakeoffData | TakeoffListItemAttached> = (
    _row,
    _cell,
    _value,
    _columnDef,
    params,
  ) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    let totalQuantity = Number(params.additionalQuantity ?? 0);
    if (params.measurements?.length) {
      params.measurements.map((measurement: TakeoffMeasurement) => {
        if (Number.isFinite(measurement.quantity)) {
          totalQuantity += measurement.quantity * measurement.multiplier;
        }
      });
    }
    const result = getDisplayQuantity({
      factor: params.measurementUnit.factor,
      quantity: totalQuantity,
    }).quantity;

    root.render(
      <div>
        {getEstimateNumberValueBasedOnSettings(
          result,
          estimateDataRef.current as Parameters<typeof getEstimateNumberValueBasedOnSettings>[1],
          "takeoff",
        )}
      </div>,
    );
    return container;
  };

  const unitTemplate: Formatter<TakeoffData | TakeoffListItemAttached> = (_row, _cell, _value, _columnDef, params) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(<div>{params.measurementUnit.selectedSymbol?.name}</div>);
    return container;
  };

  const createdByTemplate: Formatter<TakeoffData | TakeoffListItemAttached> = (
    _row,
    _cell,
    _value,
    _columnDef,
    args,
  ) => {
    const container = document.createElement("div");
    const createdById = "createdBy" in args ? args.createdBy?._id : undefined;
    container.className = "flex items-center";
    if (createdById === "0") {
      container.textContent = t("SYSTEM GENERATED");
    } else if (createdById) {
      renderUserTag(container, { id: createdById });
    }

    return container;
  };

  const executeButtonClick = (params: ExecuteQueryModalData) => (e: MouseEvent) => {
    e.stopPropagation();
    setShowConfirmExecuteQueryAgainModal({ show: true, data: params });
  };

  const executeQueryTemplate: Formatter<TakeoffData | TakeoffListItemAttached> = (
    _row,
    _cell,
    _value,
    _columnDef,
    params,
  ) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    const automationQuery = "automationQuery" in params ? params.automationQuery : undefined;
    const takeOffItemId = params.takeOffItemId;
    if (!estimateVersionIdRef.current && automationQuery?.queryUpdated) {
      root.render(
        <ToolTipComponent content={t("Associated query has been updated")} position="bottom">
          <ButtonComponent
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            onClick={executeButtonClick({ takeOffItemId, automationQuery })}
          >
            <Text>{t("Update")}</Text>
          </ButtonComponent>
        </ToolTipComponent>,
      );
    } else {
      root.render(<div></div>);
    }
    return container;
  };

  /** ********************************************** USE EFFECTS ***************************************************** */

  useEffect(() => {
    HoopsModalStatusRef.current = HoopsModalStatus;
  }, [HoopsModalStatus]);

  useEffect(() => {
    measurementsModalStatusRef.current = measurementsModalStatus;
  }, [measurementsModalStatus]);

  useEffect(() => {
    takeoffsListRef.current = takeoffsList;
    if (source === takeoffSources.VIEWER && estimateFieldsNeedToRefresh.takeoffs.length) {
      const setData = prepareEstimationFieldNeedToRefresh({
        HoopsModalStatus,
        takeoffs: [],
        estimateFieldsNeedToRefresh,
      });
      dispatch(setEstimateFieldsNeedtoRefresh(setData));
    }
  }, [takeoffsList]);

  useEffect(() => {
    if (
      source === takeoffSources.VIEWER &&
      !measurementsModalStatusRef.current.status &&
      getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv })
    ) {
      toggleShowMarkupInViewer(takeoffsList, undefined, true);
    }
  }, [takeoffsList, isModelStructureReady, isSettingsApplied]);

  useEffect(() => {
    if (source === takeoffSources.VIEWER) {
      fetchTakeoffsList();
    }

    return () => {
      if (
        source === takeoffSources.VIEWER &&
        !measurementsModalStatusRef.current.status &&
        getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv })
      ) {
        removeAllMeasurementsMarkupsFromModel();
      }
      if (source === takeoffSources.VIEWER) {
        dispatch(setEstimateFieldsNeedtoRefresh(estimateFieldsNeedToRefreshIntialState));
      }
    };
  }, []);

  useEffect(() => {
    if (estimateFieldsNeedToRefresh.takeoffs.length && source === takeoffSources.VIEWER) {
      fetchTakeoffsList();
    }
  }, [estimateFieldsNeedToRefresh.takeoffs, fetchTakeoffsList, source]);

  const handleCloseExecuteQueryModal = () => {
    setShowConfirmExecuteQueryAgainModal({ show: false, data: null });
  };

  const handleExecuteQuery = async () => {
    if (!showConfirmExecuteQueryAgainModal.data || !estimateId) return;
    setButtonState(true);
    const data = {
      takeOffItemId: showConfirmExecuteQueryAgainModal.data.takeOffItemId,
      estimateId,
    };
    const response = await upgradeTakeOffAttachedQuery(data);
    let toastData: ToastNotificationPayload;
    if (response.data.status) {
      toastData = {
        content: t("Takeoff attached query is updated successfully!"),
        type: TOAST_TYPE_SUCCESS,
      };
      refreshTakeOffAndEstimate();
    } else {
      toastData = {
        content: untranslated(response.data.errorMessage ?? "Operation failed"),
        type: TOAST_TYPE_ERROR,
      };
    }
    dispatch(showToastNotification(toastData));
    setButtonState(false);
    handleCloseExecuteQueryModal();
  };

  const executeQueryModal = () => (
    <ModalStateDialogComponent
      isVisible={showConfirmExecuteQueryAgainModal.show}
      onCancel={handleCloseExecuteQueryModal}
      headerTitle={<Text sx={{ whiteSpace: "normal" }}>{t("Update to latest Query")}</Text>}
      width="30rem"
      footerContent={
        <div className="flex gap-4">
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={handleExecuteQuery}
            disabled={buttonState}
          >
            <Text>
              {showConfirmExecuteQueryAgainModal.data?.automationQuery?.queryDeleted ? t("Confirm") : t("Update")}
            </Text>
          </ButtonComponent>
          <ButtonComponent
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            onClick={handleCloseExecuteQueryModal}
            disabled={buttonState}
          >
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      }
      modelContent={
        <div className="">
          {showConfirmExecuteQueryAgainModal.data?.automationQuery?.queryDeleted
            ? t(
                "The associated Query is deleted. If you confirm, then all measurements related to this Query will be deleted from the Takeoff.",
              )
            : t("Are you sure you want to update to latest query?")}
        </div>
      }
    />
  );

  const gridColumns = useRef(
    getTakeoffListColums({
      source,
      nameTemplate,
      quantityTemplate,
      unitTemplate,
      createdByTemplate,
      dateTemplate,
      executeQueryTemplate,
      estimateVersionId: estimateVersionId || undefined,
    }),
  );
  const getDataGrid = () => (
    <div className="overflow-auto h-full">
      <SlickGrid1
        ref={dataGrid as typeof dataGrid & React.Ref<SlickgridReactInstanceX<TakeoffGridRow>>}
        columnDefinitions={
          gridColumns.current as typeof gridColumns.current & ValidatedColumnDefinition<TakeoffGridRow>[]
        }
        enableSelectAllCheckbox
        rowHeight={source === takeoffSources.VIEWER ? 40 : 60}
        enableSortingOption
        enableRowResize
        enableFilteringOption
        enableColumnChooser
        onSelectedRowIdsChanged={selectionEvent}
        dataset={(source == takeoffSources.VIEWER && takeoffsList) as typeof takeoffsList}
        remoteDataSource={source !== takeoffSources.VIEWER ? estimateTakeoffsListUrl : undefined}
        infiniteScroll
        query={(source !== takeoffSources.VIEWER && gridQuery) as typeof gridQuery}
        toolbar={tools}
        toolbarClickHandler={toolbarClickHandler}
        onClick={recordClick as (e: GridClickEvent) => void}
        propertiesPanel={source === takeoffSources.ESTIMATE}
        propertiesDisabled={toolbarButtonDisableState.properties}
      />
    </div>
  );

  return (
    <div className="overflow-auto h-full flex-grow">
      {deleteModal()}
      {openSidebar && sidebarTakeoffId && (
        <TakeoffProperties
          takeOffItemId={sidebarTakeoffId}
          closeTakeoffsSidebar={closeTakeoffsSidebar}
          estimateId={estimateId}
        />
      )}
      {showCreateTakeoffModal.show && (
        <CreateNewTakeoffModal
          // Three boundary mismatches against the newly-typed modal, all erased:
          // `estimateId` is optional on this page but required by the modal; `closeNewTakeoffModal`
          // declares a narrower `{ refresh }` argument than the modal's `unknown`; and the edit
          // buffer carries only `name`/`description` while `TakeoffDraft` also requires `type`.
          estimateId={estimateId!}
          closeTakeoffModal={closeNewTakeoffModal as ComponentProps<typeof CreateNewTakeoffModal>["closeTakeoffModal"]}
          editData={showCreateTakeoffModal.data as ComponentProps<typeof CreateNewTakeoffModal>["editData"]}
          updateTakeOffData={updateTakeOffData}
        />
      )}
      {showDuplicateTakeoffModal && (
        <DuplicateTakeoffModal
          closeDuplicateTakeoffModal={closeDuplicateTakeoffModal}
          copyTakeoff={copyTakeoff}
          isDuplicateMeasurement={false}
        />
      )}
      <div className="h-full overflow-auto">{getDataGrid()}</div>
      {showConfirmExecuteQueryAgainModal.show && executeQueryModal()}
    </div>
  );
}

export default TakeoffsList;
